import { Box, Typography, Avatar, Chip, useTheme } from "@mui/material";
import TimelineIcon from "@mui/icons-material/Timeline";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import DevicesIcon from "@mui/icons-material/Devices";
import SyncIcon from "@mui/icons-material/Sync";
import FenceIcon from "@mui/icons-material/Fence";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import { useMemo, useState, useEffect, memo } from "react";

/**
 * Premium Activity Feed Widget
 * Real-time stream of recent events across the organization
 * Memoized to prevent unnecessary re-renders
 */
const ActivityFeed = memo(function ActivityFeed(props) {
  const { assets = [], facilities = {}, timeZone = "UTC" } = props;
  const theme = useTheme();
  const { palette = {} } = theme;
  const { primary = {}, success = {}, mode } = palette;
  const isDark = mode === "dark";

  // Brand colors
  const navyColor = primary?.main || "#002843";
  const greenColor = success?.main || "#A0C46D";

  // Theme-aware colors
  const textPrimary = isDark ? "#ffffff" : "#1e293b";
  const textSecondary = isDark ? "#94a3b8" : "#64748b";
  const borderColor = isDark ? "rgba(148, 163, 184, 0.15)" : "rgba(0, 0, 0, 0.08)";

  // Auto-refresh state
  const [lastRefresh, setLastRefresh] = useState(Date.now());

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setLastRefresh(Date.now());
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Event type configuration
  const eventConfig = {
    origin: { icon: <WarehouseIcon />, color: navyColor, label: "Origin" },
    scan: { icon: <QrCodeScannerIcon />, color: greenColor, label: "Scanned" },
    arrival: { icon: <LocationOnIcon />, color: "#3b82f6", label: "Arrived" },
    departure: { icon: <LocalShippingIcon />, color: "#8b5cf6", label: "Departed" },
    entergeofence: { icon: <FenceIcon />, color: "#10b981", label: "Entered Zone" },
    leavegeofence: { icon: <FenceIcon />, color: "#f59e0b", label: "Left Zone" },
    moving: { icon: <LocalShippingIcon />, color: "#06b6d4", label: "Moving" },
    assigned: { icon: <DevicesIcon />, color: greenColor, label: "Assigned" },
    unassigned: { icon: <DevicesIcon />, color: "#94a3b8", label: "Unassigned" },
    default: { icon: <LocalOfferIcon />, color: textSecondary, label: "Event" },
  };

  // Get event config based on event name
  const getEventConfig = (eventName) => {
    if (!eventName) return eventConfig.default;
    const key = eventName.toLowerCase().replace(/\s+/g, "");
    return eventConfig[key] || eventConfig.default;
  };

  // Generate activity feed from assets
  const activities = useMemo(() => {
    const activityList = [];

    assets?.forEach(asset => {
      if (asset?.lastEvent && asset?.timeOfLogLong) {
        const config = getEventConfig(asset.lastEvent);
        const facilityName = asset.facilityId && facilities[asset.facilityId]
          ? facilities[asset.facilityId].name
          : asset.facility || null;

        activityList.push({
          id: `${asset.assetId || asset.id}-${asset.timeOfLogLong}`,
          assetTag: asset.tag || asset.assetTag || "Unknown Asset",
          event: asset.lastEvent,
          eventConfig: config,
          facility: facilityName,
          timestamp: asset.timeOfLogLong,
          description: asset.propertiesMap?.description || null,
        });
      }
    });

    // Sort by timestamp descending (most recent first)
    return activityList
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, 20); // Limit to 20 items
  }, [assets, facilities, lastRefresh]);

  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) {
      return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }
    return date.toLocaleDateString([], { month: "short", day: "numeric" });
  };

  // Format full timestamp for tooltip
  const formatFullTime = (timestamp) => {
    if (!timestamp) return "";
    return new Date(timestamp).toLocaleString();
  };

  return (
    <Box
      component="article"
      role="feed"
      aria-label="Activity Feed"
      aria-busy={false}
      sx={{
        background: isDark
          ? "linear-gradient(145deg, #032d42 0%, #021e2d 100%)"
          : "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
        borderRadius: "16px",
        border: `1px solid ${borderColor}`,
        boxShadow: isDark
          ? "0 4px 20px rgba(0, 0, 0, 0.3)"
          : "0 4px 20px rgba(0, 0, 0, 0.06)",
        overflow: "hidden",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "4px",
          background: isDark
            ? `linear-gradient(90deg, ${greenColor} 0%, ${navyColor} 100%)`
            : `linear-gradient(90deg, ${navyColor} 0%, ${greenColor} 100%)`,
          borderRadius: "16px 16px 0 0",
        },
      }}
    >
      {/* Header – same pattern as map (icon 36 + title + subtitle) */}
      <Box
        component="header"
        sx={{
          padding: "12px 16px",
          background: "transparent",
          borderBottom: `1px solid ${borderColor}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            aria-hidden="true"
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              background: isDark
                ? `linear-gradient(135deg, ${navyColor}40 0%, ${navyColor}20 100%)`
                : `linear-gradient(135deg, ${navyColor}15 0%, ${navyColor}05 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <TimelineIcon sx={{ color: isDark ? greenColor : navyColor, fontSize: "1.2rem" }} />
          </Box>
          <Box>
            <Typography
              component="h2"
              sx={{
                fontSize: "1rem",
                fontWeight: 600,
                color: isDark ? greenColor : navyColor,
                lineHeight: 1.2,
              }}
            >
              Activity Feed
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: textSecondary }}>
              Real-time event stream
            </Typography>
          </Box>
        </Box>

        {/* Live indicator */}
        <Box
          role="status"
          aria-label="Live updates enabled"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            padding: "4px 10px",
            borderRadius: "20px",
            backgroundColor: `${greenColor}15`,
            border: `1px solid ${greenColor}30`,
          }}
        >
          <Box
            aria-hidden="true"
            sx={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              backgroundColor: greenColor,
              animation: "pulse 2s ease-in-out infinite",
              "@keyframes pulse": {
                "0%, 100%": { opacity: 1 },
                "50%": { opacity: 0.5 },
              },
            }}
          />
          <Typography sx={{ fontSize: "0.6875rem", fontWeight: 600, color: greenColor }}>
            LIVE
          </Typography>
        </Box>
      </Box>

      {/* Activity List */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          padding: "8px 12px",
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: isDark ? "rgba(148, 163, 184, 0.3)" : "rgba(0, 0, 0, 0.2)",
            borderRadius: "3px",
          },
        }}
      >
        {activities.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              padding: "40px 20px",
            }}
          >
            <Box
              sx={{
                width: "60px",
                height: "60px",
                borderRadius: "50%",
                background: `${navyColor}15`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "16px",
              }}
            >
              <TimelineIcon sx={{ fontSize: "1.75rem", color: navyColor }} />
            </Box>
            <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: textPrimary, marginBottom: "4px" }}>
              No Recent Activity
            </Typography>
            <Typography sx={{ fontSize: "0.875rem", color: textSecondary, textAlign: "center" }}>
              Events will appear here as they occur
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {activities.map((activity, index) => (
              <Box
                key={activity.id}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "12px",
                  padding: "12px 8px",
                  borderRadius: "10px",
                  transition: "all 0.2s ease",
                  position: "relative",
                  animation: `activitySlide 0.3s ease-out ${index * 0.03}s both`,
                  "@keyframes activitySlide": {
                    from: { opacity: 0, transform: "translateY(-10px)" },
                    to: { opacity: 1, transform: "translateY(0)" },
                  },
                  "&:hover": {
                    backgroundColor: isDark ? "rgba(255, 255, 255, 0.03)" : "rgba(0, 0, 0, 0.02)",
                  },
                  // Timeline connector
                  "&::before": index < activities.length - 1 ? {
                    content: '""',
                    position: "absolute",
                    left: "27px",
                    top: "44px",
                    bottom: "-12px",
                    width: "2px",
                    backgroundColor: borderColor,
                  } : {},
                }}
              >
                {/* Event Icon */}
                <Avatar
                  sx={{
                    width: "36px",
                    height: "36px",
                    backgroundColor: `${activity.eventConfig.color}15`,
                    border: `2px solid ${activity.eventConfig.color}30`,
                    "& .MuiSvgIcon-root": {
                      fontSize: "1rem",
                      color: activity.eventConfig.color,
                    },
                  }}
                >
                  {activity.eventConfig.icon}
                </Avatar>

                {/* Content */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "8px" }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                        <Typography
                          sx={{
                            fontSize: "0.8125rem",
                            fontWeight: 600,
                            color: textPrimary,
                          }}
                        >
                          {activity.assetTag}
                        </Typography>
                        <Chip
                          label={activity.eventConfig.label}
                          size="small"
                          sx={{
                            height: "20px",
                            fontSize: "0.6875rem",
                            fontWeight: 600,
                            backgroundColor: `${activity.eventConfig.color}15`,
                            color: activity.eventConfig.color,
                            border: `1px solid ${activity.eventConfig.color}30`,
                            "& .MuiChip-label": { padding: "0 8px" },
                          }}
                        />
                      </Box>
                      
                      {activity.facility && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: "4px", marginTop: "4px" }}>
                          <LocationOnIcon sx={{ fontSize: "0.75rem", color: textSecondary }} />
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              color: textSecondary,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {activity.facility}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Timestamp */}
                    <Typography
                      title={formatFullTime(activity.timestamp)}
                      sx={{
                        fontSize: "0.6875rem",
                        color: textSecondary,
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                      }}
                    >
                      {formatTime(activity.timestamp)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          padding: "10px 16px",
          borderTop: `1px solid ${borderColor}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "6px",
        }}
      >
        <SyncIcon sx={{ fontSize: "0.75rem", color: textSecondary }} aria-hidden="true" />
        <Typography sx={{ fontSize: "0.6875rem", color: textSecondary }}>
          Auto-refreshes every 30s
        </Typography>
      </Box>
    </Box>
  );
});

export default ActivityFeed;
